/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useRef, useState
} from 'react';
import {
    FolderClosed, FolderOpen, FolderPlus, Trash2, Loader2
} from 'lucide-react';
import {
    KBContext
} from 'builders/builderKnowledge/knowledgeBuilderContext/KBGlobalContext';
import {
    META_MAP_DIR
} from '../hooks/useFileHandler';
import {
    Input
} from 'shared/components/ui/input';
import MenuPopover from './MenuPopover';
import KBTooltipWrapper from './TooltipWrapper';

export function FolderComponent({
    folderName,
    folderObj,
    selectable = false,
    opennable = false,
    withMenu = false,
}) {
    const {
        metaMap,
        toggleFolder,
        handleCheckMetadata,
        handleAddFolder,
        findFilesInFolder,
        deleteObject,
        refreshList,
        objectSelector
    } = useContext(KBContext);

    const isSelected = handleCheckMetadata(folderObj.get(META_MAP_DIR), 'isSelected');
    const isDisabled = false;
    const iconOpen = handleCheckMetadata(folderObj.get(META_MAP_DIR), 'isOpened');
    const iconSize = 19;

    const getLevelPaddingStyle = () => {
        if (folderObj && metaMap.get(folderObj.get(META_MAP_DIR))) {
            const level = metaMap.get(folderObj.get(META_MAP_DIR)).level;
            return {
                paddingLeft: `${(level * 16) + 16}px`,
                paddingTop: '0.25rem',
                paddingBottom: '0.25rem',
                paddingRight: '0.5rem',
            };
        }
        return {};
    };

    const handleFolderSelect = () => {
        if (selectable) {
            objectSelector(folderObj.get(META_MAP_DIR));
        }
        if (opennable) {
            toggleFolder(folderObj.get(META_MAP_DIR));
        }
    };

    const displayIcon = () => {
        if (iconOpen) {
            return <FolderOpen className="flex-shrink-0" size={iconSize} />;
        }
        return <FolderClosed className="flex-shrink-0" size={iconSize} />;
    };

    const handleCascadedFolder = () => {
        const keys = Array.from(folderObj.keys());
        return keys.map((key) => {
            const item = folderObj.get(key);
            if (item instanceof Map && item.has(META_MAP_DIR)) {
                const metaDir = item.get(META_MAP_DIR);
                const type = handleCheckMetadata(metaDir, 'type');
                const name = handleCheckMetadata(metaDir, 'name') || key;
                const isHidden = handleCheckMetadata(metaDir, 'isHidden');

                if (isHidden) return null;

                if (type === 'folder') {
                    const isNew = handleCheckMetadata(metaDir, 'isNew');
                    const isUnnamed = name === '%__Unnamed__%';

                    if (isNew || isUnnamed) {
                        return (
                            <NewFolder
                                key={metaDir}
                                folderObj={item}
                            />
                        );
                    }
                    return (
                        <FolderComponent
                            key={metaDir}
                            folderName={name}
                            folderObj={item}
                            selectable={selectable}
                            opennable={opennable}
                            withMenu={withMenu}
                        />
                    );
                }
            }
            return null;
        });
    };

    const handleCascadedFiles = () => {
        const keys = Array.from(folderObj.keys());
        return keys.map((key) => {
            const item = folderObj.get(key);
            if (item instanceof Map && item.has(META_MAP_DIR)) {
                const metaDir = item.get(META_MAP_DIR);
                const type = handleCheckMetadata(metaDir, 'type');
                const name = handleCheckMetadata(metaDir, 'name') || key;
                const isHidden = handleCheckMetadata(metaDir, 'isHidden');

                if (isHidden) return null;

                if (type === 'file') {
                    return (
                        <FileInFolder
                            key={metaDir}
                            fileName={name}
                            fileObj={item}
                        />
                    );
                }
            }
            return null;
        });
    };

    return (
        <>
            <li
                style={getLevelPaddingStyle()}
                data-isselected={isSelected}
                data-isdisabled={isDisabled}
                className={'relative hover:bg-accent/50 w-full h-14 flex items-center justify-between list-none text-sm text-foreground data-[isdisabled=true]:text-neutral-200 data-[isdisabled=true]:cursor-not-allowed data-[isselected=true]:text-neutral-950 data-[isselected=true]:font-bold data-[isselected=true]:bg-accent '}
            >
                <div className="grid grid-cols-[auto_1fr] items-center w-full gap-2">
                    {displayIcon()}
                    <KBTooltipWrapper tooltipText={folderName}>
                        <span
                            data-isdisabled={isDisabled}
                            className="cursor-pointer block truncate w-full data-[isdisabled=true]:cursor-not-allowed"
                            onClick={() => handleFolderSelect()}
                        >
                            {folderName}
                        </span>
                    </KBTooltipWrapper>
                </div>
                <div>
                    {withMenu && <MenuPopover items={
                        [
                            {
                                label: 'Create Subfolder',
                                action: () => {
                                    handleAddFolder(folderObj);
                                },
                                icon: <FolderPlus size={15} />
                            },
                            {
                                label: 'Delete Folder',
                                action: () => {

                                    const fileList = findFilesInFolder(folderObj.get(META_MAP_DIR) + '/');
                                    deleteObject(fileList).then(() => {
                                        refreshList();
                                    });
                                },
                                icon: <Trash2 size={15} />
                            }
                        ]
                    } />}
                </div>
            </li >
            {iconOpen && (
                <>
                    {handleCascadedFolder()}
                    {handleCascadedFiles()}
                </>
            )}
        </>
    );
}

function FileInFolder({
    fileObj, fileName
}) {
    const {
        metaMap
    } = useContext(KBContext);

    const getLevelPaddingStyle = () => {
        if (fileObj && metaMap.get(fileObj.get(META_MAP_DIR))) {
            const level = metaMap.get(fileObj.get(META_MAP_DIR)).level;
            return {
                paddingLeft: `${(level * 16) + 32}px`,
                paddingTop: '0.25rem',
                paddingBottom: '0.25rem',
                paddingRight: '0.5rem',
            };
        }
        return {};
    };

    return (
        <li
            style={getLevelPaddingStyle()}
            className="relative hover:bg-accent/50 w-full h-10 flex items-center list-none text-sm text-muted-foreground"
        >
            <div className="grid grid-cols-[auto_1fr] items-center w-full gap-2">
                <span className="text-xs">📄</span>
                <KBTooltipWrapper tooltipText={fileName}>
                    <span className="block truncate w-full">
                        {fileName}
                    </span>
                </KBTooltipWrapper>
            </div>
        </li>
    );
}

export function NewFolder({
    folderObj,
    isDisabled = false,
}) {
    const {
        deleteObject, handleCheckMetadata, metaMap, saveFolder, user
    } = useContext(KBContext);

    const inputRef = useRef(null);
    const mainRef = useRef(null);
    const iconSize = 19;

    const [folderName, setFolderName] = useState('');
    const [error, setError] = useState(null);
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.select();
            mainRef.current.scrollIntoView({
                behavior: 'smooth'
            });

        }
    }, []);

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {

            deleteObject([folderObj.get(META_MAP_DIR)], true);
        } else if (e.key === 'Enter') {
            const errors = validateFolderName(folderName);
            if (errors) {
                setError(errors);
                return;
            }

            saveFolder(folderObj, folderName, user._id);
        }
    };

    const validateFolderName = (name) => {
        if (!name || typeof name !== 'string' || name.trim() === '') {
            return 'Folder name cannot be empty.';
        }
        if (name.includes('/') || name.includes('\\')) {
            return 'Folder name cannot contain slashes.';
        }
        if (name.length > 255) {
            return 'Folder name is too long.';
        }
        if (/^\.\.?$/.test(name)) {
            return 'Invalid folder name.';
        }
        if (/[<>:"|?*]/.test(name)) {
            return 'Folder name contains invalid characters.';
        }
        return null;
    };

    const resetError = () => {
        setError(null);
    };

    const handleBlur = () => {
        if (!folderName || folderName.trim() === '') {

            deleteObject([folderObj.get(META_MAP_DIR)], true);
            return;
        }

        const errors = validateFolderName(folderName);
        if (errors) {
            setError(errors);
            return;
        }

        saveFolder(folderObj, folderName, user._id);
    };

    const getLevelPaddingStyle = () => {
        if (folderObj && metaMap.get(folderObj.get(META_MAP_DIR))) {
            const level = metaMap.get(folderObj.get(META_MAP_DIR)).level;
            return {
                paddingLeft: `${(level * 16) + 11}px`,
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem',
                paddingRight: '0.5rem',
            };
        }
        return {};
    };
    return (
        <>
            <li
                ref={mainRef}
                style={getLevelPaddingStyle()}
                data-isselected={handleCheckMetadata(folderObj.get(META_MAP_DIR), 'isSelected')}
                data-isdisabled={isDisabled}
                className={'relative hover:bg-accent/50 flex items-center gap-2 list-none text-sm text-foreground data-[isdisabled=true]:text-neutral-200 data-[isdisabled=true]:cursor-not-allowed data-[isselected=true]:text-neutral-950 data-[isselected=true]:font-bold '}
            >

                {<FolderClosed className="flex-shrink-0" size={iconSize} />}
                <Input value={folderName} onChange={(e) => {
                    setFolderName(e.target.value);
                    resetError();
                }} onKeyDown={handleKeyDown} onBlur={handleBlur} ref={inputRef} placeholder="Type a folder name" className="border border-neutral-200 focus-visible:ring-0 shadow-none h-fit px-1 max-w-[150px]" type="text" />

                {error && <p className="absolute -bottom-2 right-2 text-xs text-red-600 font-thin">{error}</p>}

            </li>
        </>
    );
}

// Simplified loading component - just a spinner
export function FolderLoadingComponent({
    _width = 100 // eslint-disable-line no-unused-vars
}) {
    return (
        <li className="flex items-center justify-center gap-2 p-3 py-2 list-none text-sm text-foreground w-full">
            <Loader2 className="animate-spin text-muted-foreground" size={18} />
            <span className="text-muted-foreground text-xs">Loading...</span>
        </li>
    );
}